import { type FunctionComponent, useEffect, useRef, useState } from 'react'

import { type ApplicationCredential, ApplicationPermission } from '../../types/application'

import useCreateApplicationMutation from 'src/hooks/services/graph/mutations/useCreateApplicationMutation'
import useCreateApplicationSecretMutation from 'src/hooks/services/graph/mutations/useCreateApplicationSecretMutation'
import useDeleteApplicationSecretMutation from 'src/hooks/services/graph/mutations/useDeleteApplicationSecretMutation'
import useUpdateApplicationMutation from 'src/hooks/services/graph/mutations/useUpdateApplicationMutation'
import useApplicationsQuery from 'src/hooks/services/graph/queries/useApplicationsQuery'

import { Iconly } from 'react-iconly'
import { IoCopyOutline } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import { type RootStateOrAny } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import Button from '../Common/Button'
import Input from '../Common/Input'
import Popover from '../Common/Popover'
import PortalDrawer from '../Common/PortalDrawer'
import Select from '../Common/Select'
import Submit from '../Common/Submit'
import Switch from '../Common/Switch'

const ApplicationDrawer: FunctionComponent = (): JSX.Element => {
  const { id } = useParams()
  const navigate = useNavigate()

  // Form state for application details
  const [displayName, setDisplayName] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])

  // State for create secret form
  const [showCreateSecret, setShowCreateSecret] = useState(false)
  const [newSecretName, setNewSecretName] = useState('')
  const [newSecretExpiry, setNewSecretExpiry] = useState('')
  const [selectedExpiryMonths, setSelectedExpiryMonths] = useState<number | ''>('')

  // get selected account
  const account = useSelector((state: RootStateOrAny) => state.account)

  // Get applications data and find the current application
  const { data: applicationsData } = useApplicationsQuery(account?.id)
  const currentApplication = id ? applicationsData?.find((app) => app.appId === id) : null

  // Mutation hooks
  const createApplicationMutation = useCreateApplicationMutation()
  const updateApplicationMutation = useUpdateApplicationMutation()
  const createSecretMutation = useCreateApplicationSecretMutation(currentApplication?.appId || '')
  const deleteSecretMutation = useDeleteApplicationSecretMutation(currentApplication?.appId || '')

  // Track which secret is being deleted
  const [deletingSecretId, setDeletingSecretId] = useState<string | null>(null)

  // Hook to get previous value
  function usePreviousValue(value: any) {
    const ref = useRef()
    useEffect(() => {
      ref.current = value
    })
    return ref.current
  }

  const previousId = usePreviousValue(id)

  // Update form state when application changes
  useEffect(() => {
    if (currentApplication && id !== 'new') {
      setDisplayName(currentApplication.displayName)
      setSelectedPermissions(currentApplication.permissions || [])
    } else {
      setDisplayName('')
      setSelectedPermissions([])
    }
  }, [currentApplication, id])

  // Clear form when drawer closes
  useEffect(() => {
    if (!id) {
      setShowCreateSecret(false)
      setNewSecretName('')
      setNewSecretExpiry('')
      setSelectedExpiryMonths('')
    }
  }, [id])

  // Clear secretText when ID changes
  useEffect(() => {
    if (previousId && previousId !== 'new' && previousId !== id) {
      const previousApplication = applicationsData?.find((app) => app.appId === previousId)
      // Clear secretText from all credentials when ID changes
      previousApplication?.passwordCredentials.forEach((credential) => {
        if (credential.secretText) {
          delete (credential as any).secretText
        }
      })
    }
  }, [id, previousId, applicationsData])

  const handleClose = () => {
    const currentPath = window.location.pathname
    const newPath = currentPath.substring(0, currentPath.lastIndexOf('/'))
    navigate(newPath)
  }

  // Handler for saving application (create or update)
  const handleSaveApplication = async (e: React.FormEvent) => {
    e.preventDefault()

    const payload = {
      displayName,
      permissions: selectedPermissions
    }

    if (currentApplication?.appId) {
      // Update existing application
      updateApplicationMutation.mutate(
        {
          ...payload,
          appId: currentApplication.appId
        },
        {
          onSuccess: () => {
            handleClose()
          }
        }
      )
    } else {
      // Create new application
      createApplicationMutation.mutate(payload, {
        onSuccess: () => {
          handleClose()
          setDisplayName('')
          setSelectedPermissions([])
        }
      })
    }
  }

  // Handler for creating a new secret
  const handleCreateSecret = async () => {
    if (!currentApplication) return

    createSecretMutation.mutate(
      {
        displayName: newSecretName,
        endDateTime: newSecretExpiry
      },
      {
        onSuccess: () => {
          setShowCreateSecret(false)
          setNewSecretName('')
          setNewSecretExpiry('')
          setSelectedExpiryMonths('')
        }
      }
    )
  }

  // Handler for deleting a secret
  const handleDeleteSecret = async (keyId: string) => {
    if (!currentApplication) return

    setDeletingSecretId(keyId)
    deleteSecretMutation.mutate(keyId, {
      onSettled: () => {
        setDeletingSecretId(null)
      }
    })
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const expiryOptions = [
    { label: '3 months', value: '3' },
    { label: '6 months', value: '6' },
    { label: '1 year', value: '12' },
    { label: '2 years', value: '24' }
  ]

  // Helper to format ISO string with seconds only (no milliseconds)
  function toIsoWithSecondsOnly(date: Date): string {
    const iso = date.toISOString()
    return iso.replace(/\.\d{3}Z$/, 'Z')
  }

  const handleExpiryChange = (months: number) => {
    setSelectedExpiryMonths(months)
    const now = new Date()
    now.setMonth(now.getMonth() + months)
    setNewSecretExpiry(toIsoWithSecondsOnly(now))
  }

  const isLoading = createApplicationMutation.isLoading || updateApplicationMutation.isLoading

  const handleCopyToClipboard = async (text: string) => {
    try {
      await window.navigator.clipboard.writeText(text)
    } catch (error) {
      console.error('Failed to copy to clipboard', error)
    }
  }

  // Actions component for the drawer
  const Actions: FunctionComponent = (): JSX.Element => (
    <div className="grid grid-cols-2 gap-2">
      <Button bordered onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Submit variant="primary" form="applicationForm" loading={isLoading} loadingText="Saving...">
        {currentApplication?.appId ? 'Update' : 'Create'}
      </Submit>
    </div>
  )

  const drawerIsHidden = !id

  return (
    <PortalDrawer
      title={currentApplication?.appId ? `Edit ${currentApplication.displayName}` : 'New Application'}
      loading={isLoading}
      actions={<Actions />}
      onClose={handleClose}
      hidden={drawerIsHidden}
    >
      <form id="applicationForm" onSubmit={handleSaveApplication} className="flex flex-col gap-4">
        {currentApplication?.appId && (
          <Input
            type="text"
            name="appId"
            label="App ID"
            value={currentApplication.appId}
            readOnly
            disabled
            rightIcon={
              <button
                type="button"
                title="Copy App ID"
                className="p-0 text-th-text-secondary outline-none hover:text-th-text focus:outline-none focus:ring-0 focus-visible:outline-none"
                onClick={() => handleCopyToClipboard(currentApplication.appId)}
              >
                <IoCopyOutline className="h-5 w-5" />
              </button>
            }
          />
        )}
        <Input
          type="text"
          name="displayName"
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
          disabled={isLoading}
        />

        <div className="flex flex-col gap-1">
          <label className="ml-2 flex gap-1 text-th-text-secondary">Permissions</label>
          <div className="space-y-2 pl-4">
            {Object.values(ApplicationPermission).map((permission) => (
              <div key={permission} className="flex items-center justify-between border-b border-th-border pb-2 last:border-b-0">
                <span>{permission}</span>
                <Switch
                  id={permission}
                  checked={selectedPermissions.includes(permission)}
                  onChange={() =>
                    setSelectedPermissions((prev) => (prev.includes(permission) ? prev.filter((pid) => pid !== permission) : [...prev, permission]))
                  }
                  disabled={isLoading}
                />
              </div>
            ))}
          </div>
        </div>
      </form>

      {/* Secrets section - only show for existing applications */}
      {currentApplication?.appId && (
        <div className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold">Secrets</h3>
            <Popover
              text="Maximum of 2 secrets allowed per application. Please delete an existing secret to create a new one."
              disabled={currentApplication.passwordCredentials.length < 2}
            >
              <Button
                bordered
                className="gap-1"
                onClick={() => setShowCreateSecret(true)}
                disabled={showCreateSecret || currentApplication.passwordCredentials.length >= 2}
              >
                <Iconly set="light" name="PaperPlus" className="h-5" />
                <span>New</span>
              </Button>
            </Popover>
          </div>

          {showCreateSecret && (
            <form
              className="mb-4 flex flex-col gap-2"
              id="newAppSecretForm"
              onSubmit={(e) => {
                e.preventDefault()
                handleCreateSecret()
              }}
            >
              <Input
                type="text"
                name="secretName"
                placeholder="Secret name"
                value={newSecretName}
                onChange={(e) => setNewSecretName(e.target.value)}
                autoFocus
                required
                disabled={createSecretMutation.isLoading}
              />
              <Select
                name="secretExpiry"
                placeholder="Select expiry ..."
                options={expiryOptions}
                selected={selectedExpiryMonths?.toString()}
                onChange={(e) => {
                  handleExpiryChange(Number(e.target.value))
                }}
                required
                disabled={createSecretMutation.isLoading}
              />
              <div className="mt-2 flex w-full gap-4 border-t border-th-border pt-4 [&>*]:w-full">
                <Button bordered onClick={() => setShowCreateSecret(false)} disabled={createSecretMutation.isLoading}>
                  Cancel
                </Button>
                <Submit variant="primary" form="newAppSecretForm" loading={createSecretMutation.isLoading} loadingText="Creating...">
                  Create Secret
                </Submit>
              </div>
            </form>
          )}
          {currentApplication.passwordCredentials.length > 0 && (
            <div className="space-y-4">
              {currentApplication.passwordCredentials.map((credential: ApplicationCredential) =>
                credential.secretText ? (
                  // New secret with warning styling (separate container)
                  <div key={credential.keyId} className="bg-th-warning/10 relative rounded-lg border border-th-warning p-4">
                    <button
                      onClick={() => handleDeleteSecret(credential.keyId)}
                      disabled={deletingSecretId === credential.keyId}
                      className="absolute right-2 top-2 p-1 text-th-text-secondary hover:text-th-error disabled:cursor-not-allowed disabled:opacity-50"
                      title="Delete secret"
                    >
                      {deletingSecretId === credential.keyId ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-th-text-secondary border-t-transparent" />
                      ) : (
                        <Iconly set="light" name="Delete" className="h-4 w-4" />
                      )}
                    </button>
                    <div className="mb-2 font-semibold text-th-warning">Secret value (shown only once):</div>
                    <div className="font-mono flex items-start gap-2 break-all text-th-warning">
                      <span className="flex-1">{credential.secretText}</span>
                      <button
                        type="button"
                        title="Copy secret"
                        className="p-1 text-th-warning outline-none hover:text-th-warning focus:outline-none focus:ring-0 focus-visible:outline-none"
                        onClick={() => handleCopyToClipboard(credential.secretText!)}
                      >
                        <IoCopyOutline className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-th-text-secondary">Copy and store this value securely. You will not be able to see it again.</div>
                  </div>
                ) : (
                  // Existing secret with normal styling
                  <div key={credential.keyId} className="relative rounded-lg border border-th-border p-4">
                    <button
                      onClick={() => handleDeleteSecret(credential.keyId)}
                      disabled={deletingSecretId === credential.keyId}
                      className="absolute right-2 top-2 p-1 text-th-text-secondary hover:text-th-error disabled:cursor-not-allowed disabled:opacity-50"
                      title="Delete secret"
                    >
                      {deletingSecretId === credential.keyId ? (
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-th-text-secondary border-t-transparent" />
                      ) : (
                        <Iconly set="light" name="Delete" className="h-4 w-4" />
                      )}
                    </button>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="font-medium text-th-text-secondary">Name</label>
                        <p className="font-mono">{credential.displayName || credential.keyId}</p>
                      </div>
                      <div>
                        <label className="font-medium text-th-text-secondary">Secret</label>
                        <p className="font-mono">{credential.hint}*****</p>
                      </div>
                      <div>
                        <label className="font-medium text-th-text-secondary">Expiry</label>
                        <p>{formatDate(credential.endDateTime)}</p>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </PortalDrawer>
  )
}

export default ApplicationDrawer
